/**
 * frontend/src/pages/valuation/_FinancialsNotFilled.tsx
 *
 * VAL-FIN-WHY-EMPTY-1 — says why an upload filled nothing. Two panels, both read
 * from the last run's merge report: files the reader did not take as a financial
 * statement, and figures it found but could not place in a fiscal year. Other
 * reader warnings are internals and are not shown.
 */
import type { FinancialsReport } from '@/api/valuationFinancials'

const PART_YEAR = new Set(['ttm', 'interim'])
const isPartYear = (u: { basis?: string }) => !!u.basis && PART_YEAR.has(u.basis)

export function FinancialsNotFilled({ report }: { report: FinancialsReport | null | undefined }) {
  const notRead = (report?.documents ?? []).filter(d => d.parser === 'none')
  const unplaced = report?.unassigned_period ?? []
  if (notRead.length === 0 && unplaced.length === 0) return null
  const partYear = unplaced.some(isPartYear)
  return (
    <>
      {notRead.length > 0 && (
        <section className="space-y-1 rounded-lg border border-pal-warning-border bg-pal-warning-bg p-4">
          <h2 className="text-sm font-semibold text-pal-ink">Nothing was filled from these files</h2>
          <ul className="text-sm text-pal-ink-body">
            {notRead.map((d, i) => (
              <li key={i}>{d.filename} — not recognised as a financial statement, so no figures were read.</li>
            ))}
          </ul>
        </section>
      )}
      {unplaced.length > 0 && (
        <section className="space-y-1 rounded-lg border border-pal-warning-border bg-pal-warning-bg p-4">
          <h2 className="text-sm font-semibold text-pal-ink">Figures we could not place in a year</h2>
          <ul className="text-sm text-pal-ink-body">
            {unplaced.map((u, i) => (
              <li key={i}>
                {`${u.filename} · column ${u.period_label || '(no heading)'}`}
                {isPartYear(u) ? '' : ` · ${u.reason}`}
              </li>
            ))}
          </ul>
          {partYear && (
            <p className="text-sm text-pal-ink-body">This column covers part of a year, so it is not used as a fiscal year. No upload is needed.</p>
          )}
          {unplaced.some(u => !isPartYear(u)) && (
            <p className="text-sm text-pal-ink-body">Fix the column heading in the file and upload it again.</p>
          )}
        </section>
      )}
    </>
  )
}
